// LEGAL TEMPLATE NOTICE: This is a business / legal template and
// must be reviewed by qualified counsel before production. Update
// the bracketed placeholders (legal entity name, registered office,
// jurisdiction city/state) before going live.

import { Link } from 'react-router-dom';
import { LegalSection, LegalList } from '../../components/legal/LegalSection';

export default function TermsOfServicePage() {
  return (
    <article>
      <header className="mb-2">
        <h1 className="text-3xl sm:text-4xl font-bold text-ink-900 m-0 leading-tight">
          Terms of Service
        </h1>
        <p className="mt-2 text-sm text-ink-500 m-0">
          Last updated: May 2026
        </p>
      </header>

      <p className="mt-5 text-sm sm:text-[15px] text-ink-700 leading-relaxed">
        These Terms of Service (&ldquo;Terms&rdquo;) govern your access to
        and use of the Glass-i SaaS platform, operated by{' '}
        <strong>Glass-i [Legal Entity Name to be updated]</strong>{' '}
        (&ldquo;Glass-i&rdquo;, &ldquo;we&rdquo;, &ldquo;our&rdquo;, &ldquo;us&rdquo;).
        Please also review our{' '}
        <Link to="/privacy" className="text-brand-600 hover:text-brand-700">
          Privacy Policy
        </Link>{' '}
        and{' '}
        <Link
          to="/refund-policy"
          className="text-brand-600 hover:text-brand-700"
        >
          Refund &amp; Cancellation Policy
        </Link>
        , which are incorporated by reference.
      </p>

      <LegalSection title="1. Acceptance of terms">
        <p>
          By creating an account, accessing the website, or using the Glass-i
          SaaS application in any way, you agree to be bound by these Terms.
          If you do not agree, please do not use the service.
        </p>
      </LegalSection>

      <LegalSection title="2. About Glass-i">
        <p>
          Glass-i is a SaaS platform that helps glass processing businesses
          manage operations such as invoices, inventory, GST-related
          workflows, orders, users, and other ERP functions as enabled by
          your subscription plan.
        </p>
      </LegalSection>

      <LegalSection title="3. Eligibility and account responsibility">
        <LegalList
          items={[
            'Glass-i is intended for business use. By signing up, you confirm that you are using the service on behalf of a registered business and have the authority to bind it.',
            'You must provide accurate GSTIN, company, and contact details, and keep them up to date.',
            'The master account is responsible for the activities of all sub-users it creates and for compliance with these Terms by all users on the account.',
            'You are responsible for keeping your credentials confidential and for all use of your account.',
            'Unauthorized access, automated scraping, sharing of credentials, and circumvention of access controls are prohibited.'
          ]}
        />
      </LegalSection>

      <LegalSection title="4. Subscription plans and billing">
        <LegalList
          items={[
            'Trial Plan: ₹99 including GST, providing limited evaluation access for the period stated in-app.',
            'Standard Plan: ₹1,999 + GST per user / month, with monthly or annual billing as shown in-app. Annual billing may include a discount as displayed during checkout.',
            'Custom Plans: based on a custom code shared by Glass-i with the duration, included users, and price as displayed during checkout.',
            'Prices and plan terms may change prospectively; existing paid billing periods are unaffected.',
            'Taxes are extra unless explicitly stated as inclusive (e.g., the ₹99 trial is shown as inclusive of GST).',
            'Subscription access begins after successful payment, payment verification, and any account approval that may be required.'
          ]}
        />
      </LegalSection>

      <LegalSection title="5. Payments">
        <LegalList
          items={[
            'Payments are collected via Razorpay and our banking / payment partners.',
            'Payment must be completed and verified by the Glass-i backend before account activation. The backend re-verifies each payment with Razorpay before provisioning.',
            'Account creation and access may be subject to approval by Glass-i.',
            'Glass-i does not store full card numbers, CVV, UPI PIN, netbanking passwords, or sensitive payment authentication credentials.',
            'Tax invoices and payment receipts are generated as applicable to your plan and jurisdiction.'
          ]}
        />
      </LegalSection>

      <LegalSection title="6. Account approval">
        <LegalList
          items={[
            'After payment, an account may be created with a pending-approval status.',
            'Access to the application may be blocked until Glass-i reviews and approves the account.',
            'Glass-i may reject or suspend accounts in case of suspected fraud, invalid data, legal or compliance risk, abuse, or violations of these Terms.',
            'If an account is rejected after payment, refund handling will follow the Refund & Cancellation Policy.'
          ]}
        />
      </LegalSection>

      <LegalSection title="7. Users and sub-users">
        <LegalList
          items={[
            'The master account can create and manage sub-users according to the plan in effect.',
            'The number of users permitted is determined by the plan; adding more users may require a plan change or paid add-ons.',
            'Locking, unlocking, and surrendering users are subject to in-app rules and any cool-down periods.',
            'You are responsible for the activity of users on your account, including data they enter and actions they take.'
          ]}
        />
      </LegalSection>

      <LegalSection title="8. Acceptable use">
        <p>You agree not to:</p>
        <LegalList
          items={[
            'Misuse the platform or use it for any unlawful purpose.',
            'Attempt to gain unauthorized access to any part of the service or data of other customers.',
            'Reverse engineer, scrape, attack, or disrupt the service or its underlying systems.',
            'Upload or process illegal, infringing, or malicious content.',
            'Use the service for fraudulent billing, fake invoicing, or tax evasion.',
            'Violate any applicable Indian or other laws.'
          ]}
        />
      </LegalSection>

      <LegalSection title="9. Customer data">
        <LegalList
          items={[
            'You retain ownership of the business data you enter into Glass-i.',
            'You are responsible for the accuracy, legality, and lawful processing of customer data, including any personal data of your own customers and employees.',
            'Glass-i processes data on your behalf to provide the service in accordance with these Terms and our Privacy Policy.',
            'Data export, backup, and retention are subject to the features available in the service and may evolve over time.'
          ]}
        />
      </LegalSection>

      <LegalSection title="10. GST, tax, and compliance disclaimer">
        <p>
          Glass-i provides workflows and tooling to help you maintain
          GST-compliant records. We do not provide legal, tax, or accounting
          advice. You are responsible for verifying tax filings, invoices,
          and compliance with your chartered accountant or legal advisor.
          Glass-i is not liable for incorrect or non-compliant data entered
          by you or your users.
        </p>
      </LegalSection>

      <LegalSection title="11. Service availability">
        <p>
          We use reasonable efforts to keep the service available. Scheduled
          maintenance, unplanned outages, third-party failures, or factors
          outside our control may cause temporary downtime. We do not
          guarantee uninterrupted or error-free operation.
        </p>
      </LegalSection>

      <LegalSection title="12. Intellectual property">
        <p>
          The Glass-i software, user interface, brand, source code,
          documentation, and related materials are owned by Glass-i and / or
          its licensors. Subject to your compliance with these Terms and
          payment of applicable fees, you receive a limited, non-exclusive,
          non-transferable right to access and use the service for your
          internal business purposes during your subscription.
        </p>
      </LegalSection>

      <LegalSection title="13. Confidentiality">
        <p>
          Each party will protect the other&apos;s confidential information
          with reasonable care, use it only as needed to provide or use the
          service, and not disclose it except to personnel and advisors who
          are bound by similar obligations. You must keep your account
          credentials and any API keys or tokens confidential.
        </p>
      </LegalSection>

      <LegalSection title="14. Suspension and termination">
        <LegalList
          items={[
            'We may suspend or terminate accounts for non-payment, misuse, legal or security risk, violation of these Terms, or where required by law.',
            'Accounts may also be put into pending-approval, rejected, or suspended states as part of the lifecycle described above.',
            'On termination, access to the service will end. Where applicable, billing for completed periods is non-refundable as detailed in the Refund Policy.',
            'You may stop renewals or cancel where supported in-app.'
          ]}
        />
      </LegalSection>

      <LegalSection title="15. Limitation of liability">
        <p>
          To the maximum extent permitted by applicable law, Glass-i and its
          affiliates, suppliers, and licensors will not be liable for any
          indirect, incidental, special, punitive, or consequential damages,
          or for loss of profits, data, business, or goodwill arising out of
          or in connection with the service.
        </p>
        <p>
          Subject to the above, our total aggregate liability arising out of
          or in connection with the service is limited to the fees actually
          paid by you to Glass-i for the service in the 12 months immediately
          preceding the event giving rise to the claim. Nothing in these
          Terms excludes or limits liability that cannot be excluded or
          limited under applicable law.
        </p>
      </LegalSection>

      <LegalSection title="16. Indemnity">
        <p>
          You agree to indemnify and hold Glass-i harmless from claims,
          losses, and expenses arising out of or in connection with: misuse
          of the service by you or your users; data you upload or process
          through the service that is illegal, infringing, or otherwise in
          breach of these Terms; or your violation of applicable laws or
          third-party rights.
        </p>
      </LegalSection>

      <LegalSection title="17. Governing law and jurisdiction">
        <p>
          These Terms are governed by and construed in accordance with the
          laws of India. Subject to applicable law, the courts at{' '}
          <strong>[City, State to be updated]</strong>, India will have
          exclusive jurisdiction over any disputes arising out of or in
          connection with these Terms.
        </p>
      </LegalSection>

      <LegalSection title="18. Contact">
        <div className="rounded-xl border border-surface-200 bg-surface-50 px-4 py-3 text-sm text-ink-700 space-y-1">
          <p className="m-0">
            Email:{' '}
            <a
              href="mailto:support@glass-i.com"
              className="text-brand-600 hover:text-brand-700"
            >
              support@glass-i.com
            </a>
          </p>
          <p className="m-0">Phone: +91 98765 43210</p>
          <p className="m-0">
            Address: [Registered office address to be updated]
          </p>
        </div>
      </LegalSection>
    </article>
  );
}
